/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import { from, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { logAndCatchError } from '../../utils/rxjs-utils';
import { resolveRestApi } from '../../router/app-router';
import { fetch } from '../../service_broker/fetch';
import * as actionType from '../../store/state/actions';
import { getKeyAndFlowName, processHeaders } from '../../store/state/epics/util';
import HeaderProcessor from '../../service_broker/header-processor';
import { isAssignmentInCreateStage } from '../../utils/case-utils';
import Constants from '../../constants';
import EnvironmentInfo from '../../environment-info';
import ObjectTransformer from '../../utils/object-transformer';

import { getViewType, addRemoteHeadersIfRequired } from './helper';

const OPEN_ASSIGNMENT_DXAPI_LOG_ERROR_MESSAGE = 'Error occurred during ajax call at openAssignment Epic : ';

type ApiResponse = {
  data: {
    data: {
      caseInfo: any;
    };
  };
  headers?: { [key: string]: string };
  isReloadWithDifferentViewTypeRequired?: boolean;
  restUrl?: string;
};

const fetchOpenAssignmentInformation = ({
  restUrl,
  reqType,
  requestHeaders,
  debugInfo,
  context,
  isGetNextWork,
  doNotMergeMessagesForStatusCode
}: {
  restUrl: string;
  reqType?: string;
  requestHeaders: { [key: string]: any };
  debugInfo?: { [key: string]: string | number | boolean };
  context?: string;
  isGetNextWork?: boolean;
  doNotMergeMessagesForStatusCode: string;
}) => {
  return fetch(
    restUrl,
    {
      method: reqType,
      headers: {
        'if-match': requestHeaders && requestHeaders.etag,
        ...requestHeaders,
        ...(debugInfo && { ...HeaderProcessor.addDebugHeaders(debugInfo) })
      }
    },
    context,
    {
      doNotMergeHttpMessagesForStatusCode: isGetNextWork ? doNotMergeMessagesForStatusCode : ''
    }
  );
};

export const executeOpenAssignmentRequest = (
  actionInPayload: {
    pageName?: string;
    isTraditionalUICase?: boolean;
    headers?: { [key: string]: string };
    context?: string;
    assignmentID?: string;
    isGetNextWork?: boolean;
    debugInfo?: { [key: string]: string | number | boolean };
    isOpenStage?: boolean;
    stageID?: string;
    caseID?: string;
    jsActionQueryParams?: { [key: string]: string };
    isLastStepInCreateStage?: boolean;
  },
  options: { removeActionPayload: { [key: string]: string | number | boolean } }
  // eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  const viewType = getViewType(actionInPayload);
  const {
    headers,
    context,
    assignmentID,
    isGetNextWork,
    debugInfo,
    isOpenStage,
    stageID,
    caseID,
    jsActionQueryParams,
    isLastStepInCreateStage
  } = actionInPayload;
  const doNotMergeMessagesForStatusCode = '404';

  let restApi: string | undefined;
  let reqType: string | undefined;
  if (isGetNextWork) {
    ({ restApi, reqType } = resolveRestApi(actionType.ACTION_GETNEXTWORK, {
      viewType
    }));
  } else if (isOpenStage) {
    ({ restApi, reqType } = resolveRestApi(actionType.ACTION_OPENSTAGE, {
      caseID,
      stageID
    }));
  } else {
    ({ restApi, reqType } = resolveRestApi(actionType.ACTION_OPENASSIGNMENT, {
      assignmentID,
      viewType
    }));
  }
  const { removeActionPayload } = options;
  const defaultCasePage = EnvironmentInfo.getDefaultCasePage();
  let restUrl = '';
  let pageName = actionInPayload.pageName;
  if (actionInPayload.isTraditionalUICase) {
    pageName = 'pyDetails';
  }

  if (defaultCasePage) {
    restUrl = `${restApi}?viewType=${viewType}&pageName=${defaultCasePage}`;
  } else if (pageName) {
    restUrl = `${restApi}?viewType=${viewType}&pageName=${pageName}`;
  } else {
    restUrl = `${restApi}?viewType=${viewType}`;
  }

  if (jsActionQueryParams) {
    Object.keys(jsActionQueryParams).forEach((key) => {
      restUrl += `&${key}=${jsActionQueryParams[key]}`;
    });
  }
  const requestHeaders = addRemoteHeadersIfRequired(actionInPayload);

  return from(
    fetchOpenAssignmentInformation({
      restUrl,
      reqType,
      requestHeaders,
      debugInfo,
      context,
      isGetNextWork,
      doNotMergeMessagesForStatusCode
    })
  ).pipe(
    map((response: any) => {
      const isCoexistenceUIKITApp = EnvironmentInfo.isCoexistenceAppOfType(Constants.APP_TYPE.UIKIT);
      if (isCoexistenceUIKITApp && !isLastStepInCreateStage) {
        const isInCreateStage = isAssignmentInCreateStage(response.data.data.caseInfo);
        if (!isInCreateStage) {
          restUrl = pageName ? restUrl.replaceAll(pageName, 'pyDetails') : restUrl;
          EnvironmentInfo.setDefaultCasePage('pyDetails');
          return {
            isReloadWithDifferentViewTypeRequired: true,
            restUrl
          };
        }
        return response;
      }
      return response;
    }),
    mergeMap((response: ApiResponse) => {
      if (response?.isReloadWithDifferentViewTypeRequired) {
        return fetchOpenAssignmentInformation({
          restUrl,
          reqType,
          requestHeaders,
          debugInfo,
          context,
          isGetNextWork,
          doNotMergeMessagesForStatusCode
        });
      }
      return of(response);
    }),
    map((response) => {
      const { key, flowName } = getKeyAndFlowName(assignmentID);
      ObjectTransformer.analyseResponse(response);
      return {
        response: { ...response.data, key, flowName },
        actionInPayload,
        viewType,
        removeActionPayload,
        headers: processHeaders({ ...headers, ...response.headers })
      };
    }),
    logAndCatchError(OPEN_ASSIGNMENT_DXAPI_LOG_ERROR_MESSAGE, context)
  );
};

export default executeOpenAssignmentRequest;
